import type { Item } from "../replicache/keys";

// All dates here are JS Date objects pinned to local midnight; comparisons
// use millisecond timestamps.  Items store dates as "YYYY-MM-DD" strings.

const DAY_MS = 24 * 60 * 60 * 1000;

export type Zoom = "full-day" | "day" | "week" | "month" | "quarter";

// px per day at each zoom.  Tuned to keep the chart legible: one screen-width
// should comfortably show the relevant time horizon — at "full-day" a single
// day fills a typical mobile viewport (the "today view"); the others scale
// out from there (a couple weeks at day, several months at week, ~a year at
// month, multiple years at quarter).
export const PX_PER_DAY: Record<Zoom, number> = {
  "full-day": 320,
  day: 40,
  week: 14,
  month: 5,
  quarter: 2,
};

// --- Date helpers -----------------------------------------------------------

export function todayLocal(): Date {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

export function parseISODate(s: string): Date {
  // Accept "YYYY-MM-DD" or a longer ISO datetime ("YYYY-MM-DDTHH:…Z").
  // Earlier server builds returned DATE columns as full ISO datetimes
  // (pg-node's default DATE parser hands back a JS Date which JSON renders
  // with a T00:00:00.000Z tail); cached rows in the user's IndexedDB may
  // still carry that format until the next pull replaces them.  Slicing
  // the first 10 chars normalises both formats.
  const head = s.slice(0, 10);
  const [y, m, d] = head.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

export function formatISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Convert a chart-local X coordinate (in px, relative to viewportStart) into
 *  a calendar date, snapped to whole days. */
export function xToDate(
  xPx: number,
  viewportStart: Date,
  pxPerDay: number,
): Date {
  return addDays(viewportStart, Math.round(xPx / pxPerDay));
}

/** Locate the lane whose vertical band contains `yPx` (chart-local Y).
 *  Returns null if the cursor isn't over any lane. */
export function laneAt<T extends { top: number; height: number; groupId: string }>(
  lanes: T[],
  yPx: number,
): T | null {
  for (const l of lanes) {
    if (yPx >= l.top && yPx < l.top + l.height) return l;
  }
  return null;
}

export function startOfWeek(d: Date): Date {
  // Week starts Sunday — JS conventional.  If we want Monday-start later,
  // adjust here.
  return addDays(d, -d.getDay());
}

// --- Classification ---------------------------------------------------------

export type DatedItem = {
  item: Item;
  start: Date;
  end: Date;
  kind: "bar" | "due" | "started";
};

export function classifyItem(item: Item): DatedItem | null {
  if (item.startDate && item.dueDate) {
    return {
      item,
      start: parseISODate(item.startDate),
      end: parseISODate(item.dueDate),
      kind: "bar",
    };
  }
  if (item.dueDate) {
    const d = parseISODate(item.dueDate);
    return { item, start: d, end: d, kind: "due" };
  }
  if (item.startDate) {
    const d = parseISODate(item.startDate);
    return { item, start: d, end: d, kind: "started" };
  }
  return null;
}

// --- Lane stacking ----------------------------------------------------------

export type Placed = DatedItem & { rowIndex: number };

/** Greedy stacker: sort by start date, place each item in the lowest row
 *  whose latest end is before this item's start.  Visual padding of 1 day
 *  between items prevents touching edges. */
export function stackLane(items: DatedItem[]): {
  placed: Placed[];
  rowCount: number;
} {
  const sorted = [...items].sort(
    (a, b) => a.start.getTime() - b.start.getTime(),
  );
  const lastEnd: number[] = [];
  const placed: Placed[] = [];
  for (const d of sorted) {
    const startMs = d.start.getTime();
    const endMs = d.end.getTime() + DAY_MS; // 1-day padding
    let row = lastEnd.findIndex((e) => e <= startMs);
    if (row === -1) {
      row = lastEnd.length;
      lastEnd.push(0);
    }
    lastEnd[row] = endMs;
    placed.push({ ...d, rowIndex: row });
  }
  return { placed, rowCount: Math.max(1, lastEnd.length) };
}

/** Pixel-space greedy stacker — like stackLane but each item's footprint
 *  is computed in pixels (bar + any title that overflows past the bar's
 *  right edge).  Means a task whose title trails into the space of the
 *  next task gets pushed onto its own row instead of having its label
 *  collide with the neighbour's bar. */
export function stackLanePx(
  items: DatedItem[],
  startPx: (item: DatedItem) => number,
  endPx: (item: DatedItem) => number,
  paddingPx: number = 6,
): { placed: Placed[]; rowCount: number } {
  const sorted = [...items].sort((a, b) => startPx(a) - startPx(b));
  const lastRight: number[] = [];
  const placed: Placed[] = [];
  for (const d of sorted) {
    const s = startPx(d);
    const e = endPx(d) + paddingPx;
    let row = lastRight.findIndex((r) => r <= s);
    if (row === -1) {
      row = lastRight.length;
      lastRight.push(0);
    }
    lastRight[row] = e;
    placed.push({ ...d, rowIndex: row });
  }
  return { placed, rowCount: Math.max(1, lastRight.length) };
}

// Cached canvas 2D context for cheap text measurement.  Initialised on
// first use with the same font stack + size the Gantt bars use, so
// estimateTitlePx returns roughly what the browser will actually paint.
let _measureCtx: CanvasRenderingContext2D | null | undefined;
function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (_measureCtx !== undefined) return _measureCtx;
  if (typeof document === "undefined") {
    _measureCtx = null;
    return null;
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (ctx) {
    // Matches Tailwind default sans + the bar's text-[11px].
    ctx.font =
      "500 11px ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  }
  _measureCtx = ctx ?? null;
  return _measureCtx;
}

export function estimateTitlePx(title: string): number {
  const t = title || "(untitled)";
  const ctx = getMeasureCtx();
  if (!ctx) return t.length * 6.5;
  return ctx.measureText(t).width;
}

// --- Axis ticks -------------------------------------------------------------

export type Tick = {
  date: Date;
  label: string;
  major: boolean;
};

export function axisTicks(start: Date, end: Date, zoom: Zoom): Tick[] {
  const ticks: Tick[] = [];

  if (zoom === "full-day") {
    // One day fills the screen — there's room for a verbose label and
    // every tick is "major" because there are no minor neighbours.
    for (let d = new Date(start); d.getTime() <= end.getTime(); d = addDays(d, 1)) {
      ticks.push({
        date: new Date(d),
        label: d.toLocaleDateString(undefined, {
          weekday: "long",
          month: "short",
          day: "numeric",
        }),
        major: true,
      });
    }
    return ticks;
  }

  if (zoom === "day") {
    for (let d = new Date(start); d.getTime() <= end.getTime(); d = addDays(d, 1)) {
      ticks.push({
        date: new Date(d),
        label: `${d.toLocaleDateString(undefined, { weekday: "short" })} ${d.getDate()}`,
        major: d.getDay() === 1, // Monday
      });
    }
    return ticks;
  }

  if (zoom === "week") {
    for (let d = startOfWeek(start); d.getTime() <= end.getTime(); d = addDays(d, 7)) {
      ticks.push({
        date: new Date(d),
        label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        major: d.getDate() <= 7, // first week of the month
      });
    }
    return ticks;
  }

  if (zoom === "month") {
    let d = new Date(start.getFullYear(), start.getMonth(), 1);
    while (d.getTime() <= end.getTime()) {
      ticks.push({
        date: new Date(d),
        label: d.toLocaleDateString(undefined, { month: "short", year: "numeric" }),
        major: d.getMonth() === 0,
      });
      d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
    return ticks;
  }

  // quarter
  let d = new Date(
    start.getFullYear(),
    Math.floor(start.getMonth() / 3) * 3,
    1,
  );
  while (d.getTime() <= end.getTime()) {
    const q = Math.floor(d.getMonth() / 3) + 1;
    ticks.push({
      date: new Date(d),
      label: `Q${q} ${d.getFullYear()}`,
      major: q === 1,
    });
    d = new Date(d.getFullYear(), d.getMonth() + 3, 1);
  }
  return ticks;
}
